import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import { Editor } from '@tinymce/tinymce-react';
import {
  LuArrowLeft,
  LuArrowRight,
  LuChevronDown,
  LuChevronLeft,
  LuChevronRight,
  LuFileText,
  LuPrinter,
  LuUsers2,
} from 'react-icons/lu';
import { Formik } from 'formik';
import pluralize from 'pluralize';

import { isFeatureEnabled, useMediaQuery } from 'helpers';
import { Button, FormSelectField, PageHeader } from '_components/_core';

import { VARIABLES } from '_components/Settings/components/Contracts/utilities';

import { ValidationSchema, extractCustomVariablesWithTypes } from './utilities';
import { getEditorConfig } from './utilities/editorUtils';
import { CustomCard, CustomCardFooter, CustomCardBody, CustomCardHeader } from './styles';
const EDITOR_SCRIPT_PATH = `${process.env.PUBLIC_URL}/tinymce/tinymce.min.js`;
const EDITOR_TRANSLATION_PATH = `${process.env.PUBLIC_URL}/assets/langs/pt_BR.js`;

pluralize.addIrregularRule('contrato', 'contratos');

function ContractGeneration({
  activeCompany,
  allContacts,
  contracts,
  user,
  isLoadingRecipients,
  isLoadingContracts,
  onFetchAllContracts,
  onFetchContact,
  onFetchContacts,
}) {
  const history = useHistory();
  const location = useLocation();

  const { isMobile } = useMediaQuery();

  const editorRef = useRef(null);

  const [currentContactIndex, setCurrentContactIndex] = useState(0);
  const [customVariableValues, setCustomVariableValues] = useState({});
  const [currentContactName, setCurrentContactName] = useState('');
  const [isPreviewReady, setIsPreviewReady] = useState(false);

  const [isGenerating, setIsGenerating] = useState(false);
  const [showContacts, setShowContacts] = useState(true);
  const [showContracts, setShowContracts] = useState(true);

  const params = new URLSearchParams(location.search);
  const contact_ids = params.getAll('contact_ids');

  useEffect(() => {
    onFetchAllContracts();
    onFetchContacts();
  }, [onFetchAllContracts, onFetchContacts, activeCompany]);

  const handleGoToContracts = useCallback(() => {
    history.push('/contratos');
  }, [history]);

  const handleGoToContacts = useCallback(() => {
    history.push('/contatos');
  }, [history]);

  const contractsOptions = useMemo(() => {
    if (!contracts || isEmpty(contracts)) return [];

    return sortBy(contracts, 'name').map(contract => ({
      value: contract._id,
      label: contract.name,
    }));
  }, [contracts]);

  const contactsOptions = useMemo(() => {
    if (!allContacts || isEmpty(allContacts)) return [];

    return allContacts.map(contact => ({
      value: contact.id,
      label: contact.name,
    }));
  }, [allContacts]);

  const initialValues = useMemo(() => {
    let selectedContacts = [];

    if (!isEmpty(contact_ids)) {
      selectedContacts = contactsOptions.filter(option =>
        contact_ids.includes(String(option.value)),
      );
    }

    return {
      contacts: selectedContacts,
      contract_id: null,
    };
  }, [contact_ids, contactsOptions]);

  const handlePreview = useCallback(
    (contact, contract) => {
      if (!editorRef.current) {
        return;
      }

      if (!contract) {
        alert('Selecione um modelo de contrato.');
        return;
      }

      setCurrentContactName(contact.label);

      setIsGenerating(true);

      onFetchContact(contact.value, foundContact => {
        try {
          let original = contract.content || '';

          VARIABLES.forEach(variable => {
            const { name, getter } = variable;

            let value = 'Não informado';

            if (getter) {
              value = getter({
                company: activeCompany,
                user,
                contact: foundContact || {},
              });
            }

            original = original.replaceAll(name, value);
          });

          const remainingVars = extractCustomVariablesWithTypes(original);

          if (remainingVars.length > 0) {
            const updatedVarValues = { ...customVariableValues };

            remainingVars.forEach(variable => {
              if (!updatedVarValues[variable.name]) {
                updatedVarValues[variable.name] = '';
              }
            });

            setCustomVariableValues(updatedVarValues);
          }

          let highlightedContent = original;

          remainingVars.forEach(variable => {
            // Handle both formats: [variable_name] and [variable_name|TYPE]
            const variablePattern = variable.fullName
              ? `[${variable.fullName}]`
              : `[${variable.name}${variable.type !== 'TEXTO' ? `|${variable.type}` : ''}]`;

            highlightedContent = highlightedContent.replaceAll(
              variablePattern,
              `<span class="contract-variable" data-variable="${variable.name}" data-variable-type="${variable.type}">${variablePattern}</span>`,
            );
          });

          editorRef.current.setContent(highlightedContent);

          setIsPreviewReady(true);
        } catch (error) {
          console.error('Error generating contract preview:', error);
          toast.error('Erro ao gerar a pré-visualização do contrato.');
        } finally {
          setIsGenerating(false);
        }
      });
    },
    [activeCompany, user, onFetchContact, customVariableValues],
  );

  const navigateToContact = useCallback(
    (contacts, index, contract) => {
      if (contacts.length === 0) return;

      const newIndex = Math.max(0, Math.min(index, contacts.length - 1));

      setCurrentContactIndex(newIndex);

      const contact = contacts[newIndex];

      if (contact) {
        handlePreview(contact, contract);
      }
    },
    [handlePreview],
  );

  const handlePreviousContact = useCallback(
    (contacts, contract) => {
      navigateToContact(contacts, currentContactIndex - 1, contract);
    },
    [currentContactIndex, navigateToContact],
  );

  const handleNextContact = useCallback(
    (contacts, contract) => {
      navigateToContact(contacts, currentContactIndex + 1, contract);
    },
    [currentContactIndex, navigateToContact],
  );

  const handleFormSubmit = useCallback(
    values => {
      const { contract_id, contacts } = values;

      if (isEmpty(contract_id)) {
        return;
      }

      const contract = contracts.find(c => c._id === contract_id);

      setCurrentContactIndex(0);

      if (!isEmpty(contacts)) {
        handlePreview(contacts[0], contract);
      }
    },
    [handlePreview, contracts],
  );

  const handlePrint = useCallback(() => {
    try {
      const originalTitle = document.title;

      document.title = `Contrato - ${currentContactName}`;

      editorRef.current.execCommand('mcePrint');

      setTimeout(() => {
        document.title = originalTitle;
      }, 1000);
    } catch (error) {
      console.error('Error printing contract:', error);
    }
  }, [currentContactName]);

  if (!isFeatureEnabled('contracts', user.flags)) {
    return null;
  }

  return (
    <Container fluid className="content-wrapper">
      <PageHeader title="Gerar contratos" variant="small" />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={ValidationSchema}
        validateOnChange
        validateOnBlur
      >
        {({ values, handleSubmit }) => {
          const selectedContract = contracts.find(c => c._id === values.contract_id);

          return (
            <>
              <Row>
                <Col xs={12} sm={12} md={4}>
                  <CustomCard>
                    <CustomCardHeader>
                      <span className="d-flex align-items-center">
                        <LuUsers2 className="mr-2 text-muted" />
                        <h3>Selecione os contatos</h3>
                      </span>
                      <Button
                        variant="link"
                        size="sm"
                        className="m-0 p-0"
                        onClick={() => setShowContacts(!showContacts)}
                      >
                        {showContacts ? (
                          <LuChevronDown size={16} />
                        ) : (
                          <LuChevronLeft size={16} />
                        )}
                      </Button>
                    </CustomCardHeader>
                    {showContacts && (
                      <div>
                        <CustomCardBody>
                          <FormSelectField
                            name="contacts"
                            options={contactsOptions}
                            isMulti
                            value={values.contacts}
                            isLoading={isLoadingRecipients}
                            disabled={isLoadingRecipients}
                          />
                        </CustomCardBody>
                        <CustomCardFooter>
                          <Button
                            onClick={handleGoToContacts}
                            variant="link"
                            className="m-0 p-0 d-flex align-items-center"
                            size="sm"
                          >
                            Ver contatos
                            <LuChevronRight className="ml-1" />
                          </Button>
                        </CustomCardFooter>
                      </div>
                    )}
                  </CustomCard>
                  <CustomCard className="mt-3">
                    <CustomCardHeader>
                      <span className="d-flex align-items-center">
                        <LuFileText className="mr-2 text-muted" />
                        <h3>Selecione um modelo</h3>
                      </span>
                      <Button
                        variant="link"
                        size="sm"
                        className="m-0 p-0"
                        onClick={() => setShowContracts(!showContracts)}
                      >
                        {showContracts ? (
                          <LuChevronDown size={16} />
                        ) : (
                          <LuChevronLeft size={16} />
                        )}
                      </Button>
                    </CustomCardHeader>
                    {showContracts && (
                      <div>
                        <CustomCardBody>
                          <FormSelectField
                            name="contract_id"
                            options={contractsOptions}
                            isLoading={isLoadingContracts}
                            disabled={isLoadingContracts}
                          />
                        </CustomCardBody>
                        <CustomCardFooter>
                          <Button
                            onClick={handleGoToContracts}
                            variant="link"
                            className="m-0 p-0 d-flex align-items-center"
                            size="sm"
                          >
                            Ver modelos
                            <LuChevronRight className="ml-1" />
                          </Button>
                        </CustomCardFooter>
                      </div>
                    )}
                  </CustomCard>
                  <Button
                    variant="success-2"
                    className="mt-3 w-100"
                    onClick={handleSubmit}
                    disabled={isGenerating}
                    isLoading={isGenerating}
                  >
                    {`Gerar ${values.contacts.length} ${pluralize('contrato', values.contacts.length)}`}
                  </Button>
                </Col>
                <Col
                  xs={12}
                  sm={12}
                  md={8}
                  style={{
                    visibility:
                      values.contract_id && !isGenerating && isPreviewReady
                        ? 'visible'
                        : 'hidden',
                  }}
                >
                  <div>
                    {values.contacts.length > 0 && (
                      <div
                        style={{ flex: 1 }}
                        className="d-flex align-items-center justify-content-center mt-3 mb-3"
                      >
                        <span className="d-flex flex-column align-items-center justify-content-center">
                          <div className="d-flex align-items-center">
                            <Button
                              variant="white"
                              className="m-0 p-1 pl-2 pr-2 d-flex align-items-center mr-2"
                              onClick={() =>
                                handlePreviousContact(values.contacts, selectedContract)
                              }
                              disabled={
                                currentContactIndex === 0 || values.contacts.length <= 1
                              }
                            >
                              <LuArrowLeft className="mr-2" />
                              Anterior
                            </Button>
                            <small className="text-muted m-0 p-0">
                              {currentContactIndex + 1} de {values.contacts.length}
                            </small>
                            <Button
                              variant="white"
                              className="m-0 p-1 pl-2 pr-2 d-flex align-items-center ml-2"
                              onClick={() =>
                                handleNextContact(values.contacts, selectedContract)
                              }
                              disabled={
                                currentContactIndex === values.contacts.length - 1 ||
                                values.contacts.length <= 1
                              }
                            >
                              Próximo
                              <LuArrowRight className="ml-2" />
                            </Button>
                          </div>
                          <h4 className="m-0 p-0 mt-3">
                            {values.contacts[currentContactIndex]?.label || ''}
                          </h4>
                        </span>
                      </div>
                    )}

                    <Editor
                      {...getEditorConfig({
                        isMobile,
                        editorRef,
                        editorScriptPath: EDITOR_SCRIPT_PATH,
                        translationPath: EDITOR_TRANSLATION_PATH,
                      })}
                    />

                    {isPreviewReady && (
                      <div className="print-section mt-3 mb-5 d-flex align-items-center">
                        <Button
                          variant="white"
                          className="d-flex align-items-center mr-2"
                          onClick={handlePrint}
                        >
                          <LuPrinter className="mr-2 text-success" />
                          Imprimir
                        </Button>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </>
          );
        }}
      </Formik>
    </Container>
  );
}

ContractGeneration.defaultProps = {
  contracts: [],
  isLoadingRecipients: false,
  isLoadingContracts: false,
};

ContractGeneration.propTypes = {
  activeCompany: PropTypes.object.isRequired,
  user: PropTypes.object,
  contracts: PropTypes.array,
  allContacts: PropTypes.array,
  isLoadingRecipients: PropTypes.bool,
  isLoadingContracts: PropTypes.bool,
  onFetchAllContracts: PropTypes.func.isRequired,
  onFetchContact: PropTypes.func.isRequired,
  onFetchContacts: PropTypes.func.isRequired,
};

export default ContractGeneration;
